import { CircleAlert, X } from "lucide-react";
import { DateTime } from "luxon";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Datepicker } from "../../shared/datepicker/Datepicker";
import { currenciesMap } from "../invoiceArea/currencies";
import { SelectTransaction } from "@/database/schema";
import { Currencies, InvoicesHistoryData } from "@/types";
import { formatPrice, useClickOutside } from "@/utils";

type Props = {
    invoice: InvoicesHistoryData,
    action: (amount: number, invoiceNumber: string, currency: Currencies, date: Date) => Promise<void>,
    close: () => void
}

export const AddTransaction = ({ invoice: { transactions, totalToPay, currency, invoiceNumber }, close, action }: Props) => {
    const [paidDate, setPaidDate] = useState(DateTime.now());
    const { register, setValue, handleSubmit, watch, reset, formState: { errors } } = useForm<{ amount: number | null, paidDate: DateTime }>({
        defaultValues: {
            paidDate: paidDate
        }
    });
    const formAreaRef = useRef<HTMLDivElement>(null);
    const addAmount = watch("amount") ?? 0;

    const transactionsSum = (transactions: SelectTransaction[]) => {
        return transactions.reduce((acc, obj) => acc + obj.amount, 0);
    }

    const required = (transactions: SelectTransaction[], total: number) => {
        const amount = isNaN(addAmount) ? 0 : addAmount;
        const res = total - transactionsSum(transactions) - amount;
        if (isNaN(res)) return total;
        return res;
    }
    useClickOutside(formAreaRef, () => {
        close();
    });

    return <div className="overlay">
        <div className="add-transaction" ref={formAreaRef}>
            <form onSubmit={handleSubmit(async (data) => {
                action(data.amount as number, invoiceNumber, currency, data.paidDate.toJSDate())
                close();
            })}>
                <div className="add-transaction__head">
                    <span className="add-transaction__head-text">
                        <b>New transaction</b>
                    </span>
                    <button type="button" onClick={() => {
                        reset();
                        close()
                    }}>
                        <X width={18} height={18} />
                    </button>
                </div>
                <div className="add-transaction__content">
                    <div className="add-transaction__inputs">
                        <div className="add-transaction__date-input">
                            <span>Date paid:</span>
                            <Datepicker
                                type="dateIssuedSelector"
                                name="Date paid:"
                                dateIssued={paidDate}
                                dateDue={paidDate}
                                isError={errors.paidDate ? true : false}
                                setValue={(date: DateTime) => {
                                    setPaidDate(date);
                                    setValue("paidDate", date)
                                }} />
                            <input className="date-input" type="date"  {...register("paidDate")} />
                        </div>
                        <div className="add-transaction__amount">
                            <div className="add-transaction__amount-inner">
                                <div className="add-transaction__amount-controls">
                                    <span>Amount:</span>
                                    {required(transactions, totalToPay) > 0 &&
                                        <button type="button" onClick={() => setValue("amount", totalToPay - transactionsSum(transactions))}>
                                            Paid in full
                                        </button>}

                                </div>
                                <div className="add-transaction__input-container">
                                    {currenciesMap.get(currency)?.symbol}
                                    <input className="add-transaction__amount-input"
                                        autoFocus
                                        placeholder="0.00"
                                        step="0.01"
                                        type="number" {...register("amount", {
                                            required: true,
                                            valueAsNumber: true,
                                            validate: (value) => value !== 0
                                        })}
                                        autoComplete="off" />
                                </div>

                            </div>
                        </div>
                    </div>
                    <div className="add-transaction__button-container">
                        {required(transactions, totalToPay) < 0 && <span className="add-transaction__overpay">
                            <CircleAlert width={14} height={14} /> Overpay
                            <b> {currenciesMap.get(currency)?.symbol}
                                {formatPrice(Math.abs(required(transactions, totalToPay)))}</b>
                        </span>}
                        {required(transactions, totalToPay) > 0 && <span className="add-transaction__required">Required amount:
                            <b>
                                {currenciesMap.get(currency)?.symbol}
                                {formatPrice(required(transactions, totalToPay))}
                            </b>
                        </span>}
                        <button type="submit" className="add-transaction__add" disabled={errors.amount ? true : false}>
                            Add transaction
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div >
}
